import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import {
    LoadingSpinnerService,
    ProjectMandateService,
    ProjectService,
    QuickscanDefinitionService,
    QuickscanDraftService,
    TisDefinitionService
} from '../../services';
import {
    IQuestionData,
    IQuestionType,
    ProjectDetails,
    ProjectMandateDetails,
    QuickscanDefinition,
    TisDefinition
} from '../../models';
import { IScrollNavigationItem } from '../scroll-navigation/scroll-navigation.data';
import { IAttachment, ITisAnswerValue } from '../../models/project/project-data';
import { Question } from '../../models/definition/question';
import { RouterService } from '../../services/router/router.service';
import { ProjectSteps } from '../../enums';
import { TisPossibleAnswer } from '../../models/definition/tis-possible-answer';
import { DepartmentService } from '../../services/department/department.service';
import { IDepartment } from '../../models/department/department';
import { TisCalculationOperator } from '../../models/definition/tis-calculation-operator';
import { QuestionAnswerUtil, QuestionFormAnswer } from '../../utils/question-answer.util';

@Component({
    selector: 'app-project-tis',
    templateUrl: './project-tis.component.html',
    styleUrls: ['./project-tis.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectTisComponent implements OnInit {
    protected projectService = inject(ProjectService);
    private quickscanDefinitionService = inject(QuickscanDefinitionService);
    private tisDefinitionService = inject(TisDefinitionService);
    private projectMandateService = inject(ProjectMandateService);
    private routerService = inject(RouterService);
    private departmentService = inject(DepartmentService);
    protected quickscanDraftService = inject(QuickscanDraftService);
    public loadingSpinnerService = inject(LoadingSpinnerService);


    public navItems: IScrollNavigationItem[] = [];
    public quickscanDefinition?: QuickscanDefinition;
    public tisDefinition?: TisDefinition | QuickscanDefinition;
    @Input() public projectDetails: ProjectDetails;
    public mandateDetails: ProjectMandateDetails;
    public isPreviousUrlMandating = false;
    public departments: IDepartment[] = [];

    async ngOnInit() {
        this.loadingSpinnerService.show();

        this.departments = await this.departmentService.getDepartments(this.projectDetails.id);

        await this.loadDefinitions();

        this.navItems.push(
            ...this.tisDefinition.categories.map((category, index) => ({
                label: '' + (index + 1) + '. ' + category.name,
                elementId: 'ThemeTitleTIS' + category.id
            }))
        );

        if (!this.projectDetails.usesProjectAnalysisDefinition()) {
            await this.projectMandateService.getMandateDetails(this.projectDetails)
                .then((mandateDetails) => {
                    this.mandateDetails = mandateDetails;
                });
        }

        this.isPreviousUrlMandating = this.routerService.checkIfFromMandating();

        if (this.navItems && this.tisDefinition) {
            this.loadingSpinnerService.hide();
        }
    }

    protected async loadDefinitions(): Promise<void> {
        this.tisDefinition = await this.tisDefinitionService.getTisDefinition(
            this.projectDetails.companyTypeId,
            this.projectDetails.tisDefinitionVersionId,
            this.projectDetails.tisLabelShort
        );

        this.quickscanDefinition = await this.quickscanDefinitionService.getQuickscanDefinition(
            this.projectDetails.companyTypeId,
            this.projectDetails.quickscanDefinitionVersionId
        );
    }

    protected getAnswerValue(question: IQuestionData): ITisAnswerValue {
        if (this.projectDetails.usesProjectAnalysisDefinition()) {
            return this.projectDetails.projectAnalysisAnswers.getValueForQuestion(question);
        }

        return this.projectDetails.getTisAnswerForQuestion(question).value;
    }

    protected setAnswerValue(question: IQuestionData, value: ITisAnswerValue): void {
        if (this.projectDetails.usesProjectAnalysisDefinition()) {
            this.projectDetails.projectAnalysisAnswers.updateAnswer(question, value);
            this.saveAnalysisDraft();
        } else {
            this.projectDetails.updateTisAnswer(question, value);
        }
    }

    /**
     * The analysis answers are only sent to the backend via the page save
     * button; the sessionStorage draft protects unsaved changes in between.
     * Shares the draft key with the quickscan step: both steps snapshot the
     * complete project analysis answer store.
     */
    protected saveAnalysisDraft(): void {
        if (this.projectDetails.usesProjectAnalysisDefinition()) {
            this.quickscanDraftService.save(this.projectDetails.id, this.projectDetails.projectAnalysisAnswers.getAnswers());
        }
    }

    protected isDependencyMet(question: Question): boolean {
        if (question.dependentQuestion == null) {
            return true;
        }

        const dependentQuestion = this.tisDefinition.questions
            .find(q => q.id === question.dependentQuestion.id);
        if (dependentQuestion == null) {
            return true;
        }

        if (!this.isDependencyMet(dependentQuestion)) {
            return false;
        }

        const dependentValue = this.getAnswerValue(dependentQuestion);

        if (dependentQuestion.type === IQuestionType.BOOL || dependentQuestion.type === IQuestionType.CHECKBOX) {
            return dependentValue.bool === question.dependentQuestion.boolValue;
        } else if (dependentQuestion.type === IQuestionType.MULTIPLECHOICE) {
            return dependentValue.answerId === question.dependentQuestion.answerId;
        } else {
            throw new Error(`Unexpected type ${dependentQuestion.type}`);
        }
    }

    /**
     * Multi-answer values are arrays; they are cached because returning a
     * fresh array on every change-detection cycle loops change detection.
     */
    private readonly multiAnswerCache = new Map<number, number[]>();

    public answerChanged(answer: string | Date | number[], question: IQuestionData) {
        if (this.isMultiAnswerQuestion(question)) {
            this.projectDetails.projectAnalysisAnswers.updateMultiAnswer(question, (answer as number[]) ?? []);
            this.multiAnswerCache.delete(question.id);
            this.saveAnalysisDraft();
            return;
        }

        const tisAnswerValue = this.convertFormAnswerToRestTisValue(question, answer as string | Date);

        this.setAnswerValue(question, tisAnswerValue);

        const dependentQuestions = this.tisDefinition.questions.filter(q => q.calculation?.leftQuestionTempId === question.tempId || q.calculation?.rightQuestionTempId === question.tempId);
        for (const dependentQuestion of dependentQuestions) {
            this.recalculate(dependentQuestion);
        }
    }

    private recalculate(calculationQuestion: Question) {
        const leftQuestion = this.tisDefinition.questions.find(q => q.tempId === calculationQuestion.calculation.leftQuestionTempId);
        const rightQuestion = this.tisDefinition.questions.find(q => q.tempId === calculationQuestion.calculation.rightQuestionTempId);

        const leftValue = this.getAnswerValue(leftQuestion).integer;
        const rightValue = this.getAnswerValue(rightQuestion).integer;

        if (typeof leftValue !== 'number' || typeof rightValue !== 'number') {
            this.setAnswerValue(calculationQuestion, { integer: null, attachments: [] });
        } else {
            const computedValue = this.applyOperator(calculationQuestion.calculation.operator, leftValue, rightValue);
            this.setAnswerValue(calculationQuestion, { integer: computedValue, attachments: [] });
        }
    }

    private applyOperator(operator: TisCalculationOperator, left: number, right: number) {
        switch (operator) {
            case TisCalculationOperator.SUBTRACT:
                return left - right;
            default:
                throw new Error(`Unexpected operator ${operator}`);
        }
    }

    public attachmentsChanged(attachments: IAttachment[], question: IQuestionData) {
        if (this.projectDetails.usesProjectAnalysisDefinition()) {
            this.projectDetails.projectAnalysisAnswers.updateAttachments(question, attachments);
            this.saveAnalysisDraft();
            return;
        }

        const tisAnswerValue = this.projectDetails.getTisAnswerForQuestion(question).value;
        tisAnswerValue.attachments = attachments;

        this.projectDetails.updateTisAnswer(question, tisAnswerValue);
    }

    public getInitialValue(question: Question) {
        if (this.isMultiAnswerQuestion(question)) {
            if (!this.multiAnswerCache.has(question.id)) {
                this.multiAnswerCache.set(
                    question.id,
                    this.projectDetails.projectAnalysisAnswers.getAnswerIdsForQuestion(question)
                );
            }
            return this.multiAnswerCache.get(question.id);
        }

        return this.convertRestTisValueToFormAnswer(question, this.getAnswerValue(question));
    }


    protected isMultiAnswerQuestion(question: IQuestionData): boolean {
        return question.isMultiAnswer === true
            && question.type === IQuestionType.MULTIPLECHOICE
            && this.projectDetails.usesProjectAnalysisDefinition();
    }

    public getUriForStep(stepId: ProjectSteps): string {
        return `/project/${this.projectDetails.id}/step/${stepId}`;
    }

    public quickscanSubmitted(): boolean {
        return !(this.mandateDetails && !this.mandateDetails.preconditions.quickscanNotSubmitted);
    }

    public canEditTis(): boolean {
        return !this.projectDetails.isMandated()
            && !this.projectDetails.isOnHold()
            && !this.projectDetails.isClosed()
            && !this.projectDetails.isInMandateProcess()
            && this.projectService.canEditTis(this.projectDetails);
    }

    public getQuestionAttachments(question: Question): IAttachment[] {
        return this.getAnswerValue(question).attachments;
    }

    public hasEmptyRequiredQuestion(): boolean {
        // Only check questions from visible categories that have dependencies met
        return this.tisDefinition.categories
            .flatMap(category => category.questions)
            .filter(question => question.isRequired) // Only check required questions
            .filter(question => this.isDependencyMet(question)) // Only check questions with met dependencies
            .some(question => !this.isQuestionFilled(question)); // Check if any of them are not filled
    }

    protected isQuestionFilled(question: Question): boolean {
        return QuestionAnswerUtil.isAnswered(question, this.getInitialValue(question));
    }


    private convertFormAnswerToRestTisValue(question: IQuestionData, answer: string | Date | number | boolean | TisPossibleAnswer): ITisAnswerValue {
        return QuestionAnswerUtil.toAnswerValue(question, answer as QuestionFormAnswer);
    }

    private convertRestTisValueToFormAnswer(question: IQuestionData, restAnswer: ITisAnswerValue): string | Date | number | boolean {
        return QuestionAnswerUtil.toFormAnswer(question, restAnswer);
    }
}
